import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Grid, Typography, Tabs, Tab, Button, Chip,
  FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup,
  LinearProgress, Stack, Divider, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { useApi } from "../hooks/useApi.js";
import { formLabel } from "../lib/labels.js";
import { currentPeriod, type PartnerReduced } from "@kappa-v2/contracts";

const eur = (n?: number) => `€ ${(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

// Lista a scorrimento infinito (solo mobile): mostra `initial` elementi e ne
// aggiunge `step` man mano che ci si avvicina al fondo. Il sentinella ha un
// rootMargin ampio → il caricamento parte PRIMA di toccare il fondo, così lo
// scroll resta continuo e l'utente non percepisce il caricamento "a pezzetti".
// I dati sono già tutti in memoria: qui si rende progressivamente, non si fa rete.
function InfiniteList<T>({ items, initial = 25, step = 10, resetKey, keyOf, render }: {
  items: T[];
  initial?: number;
  step?: number;
  resetKey: React.Key;
  keyOf: (item: T, index: number) => React.Key;
  render: (item: T, index: number) => React.ReactNode;
}) {
  const [count, setCount] = useState(initial);
  // Cambiano i filtri (resetKey) → si riparte dall'inizio.
  useEffect(() => { setCount(initial); }, [resetKey, initial]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMore = count < items.length;
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setCount((c) => Math.min(c + step, items.length)); },
      { rootMargin: "600px 0px" } // anticipo: carica ~600px prima del fondo
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, step, items.length]);
  return (
    <>
      {items.slice(0, count).map((it, i) => (
        <React.Fragment key={keyOf(it, i)}>{render(it, i)}</React.Fragment>
      ))}
      {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
    </>
  );
}

// Progressivo di riga. Con `groupOf` le righe restano raggruppate (ordine dei gruppi
// = ordine in cui compaiono in `groupOrder`, default `rows`), la numerazione riparte
// da 1 a ogni gruppo e `gapBefore` segnala l'inizio di un nuovo gruppo (per lo
// spazio vuoto tra due periodi).
interface NumberedRow<T> { row: T; n: number; gapBefore: boolean }
function numberRows<T>(rows: T[], groupOf?: (row: T) => string, groupOrder: T[] = rows): NumberedRow<T>[] {
  if (!groupOf) return rows.map((row, i) => ({ row, n: i + 1, gapBefore: false }));
  const rank = new Map<string, number>();
  groupOrder.forEach((r) => { const g = groupOf(r); if (!rank.has(g)) rank.set(g, rank.size); });
  const grouped = [...rows].sort((a, b) => rank.get(groupOf(a))! - rank.get(groupOf(b))!); // sort stabile
  let n = 0;
  return grouped.map((row, i) => {
    const gapBefore = i > 0 && groupOf(row) !== groupOf(grouped[i - 1]!);
    n = gapBefore ? 1 : n + 1;
    return { row, n, gapBefore };
  });
}

const GROUP_GAP = <Box sx={{ height: 16, bgcolor: "#FAFAFA", borderTop: "1px solid rgba(0,0,0,.06)" }} />;

// Colonna della tabella desktop a scorrimento infinito.
type SortDir = "asc" | "desc";
interface InfCol<T> {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  width?: number;     // larghezza fissa in px
  flex?: number;      // colonna flessibile
  minWidth?: number;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number; // se presente, la colonna è ordinabile
}

// Tabella desktop: intestazioni cliccabili (ordinamento client istantaneo) +
// corpo a scorrimento infinito (InfiniteList: 25 e poi +10 con prefetch).
// Niente DataGrid → nessun limite di 100 righe.
// `numbered` antepone la colonna "#" (progressivo sull'ordine visualizzato);
// `groupOf` mantiene le righe raggruppate (l'ordinamento vale dentro al gruppo)
// e fa ripartire il progressivo a ogni gruppo.
function SortableInfiniteTable<T>({ rows, columns, resetKey, keyOf, initialSort, numbered, groupOf }: {
  rows: T[];
  columns: InfCol<T>[];
  resetKey: React.Key;
  keyOf: (row: T, i: number) => React.Key;
  initialSort?: { key: string; dir: SortDir };
  numbered?: boolean;
  groupOf?: (row: T) => string;
}) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(initialSort ?? null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const sv = col.sortValue;
    const arr = [...rows].sort((a, b) => {
      const va = sv(a), vb = sv(b);
      const cmp = (typeof va === "number" && typeof vb === "number")
        ? va - vb
        : String(va).localeCompare(String(vb), "it");
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, sort, columns]);
  // Ordine dei gruppi fisso (quello di `rows`): l'ordinamento per colonna vale dentro al gruppo.
  const items = useMemo(() => numberRows(sorted, groupOf, rows), [sorted, groupOf, rows]);

  const onSort = (c: InfCol<T>) => {
    if (!c.sortValue) return;
    setSort((s) => (s && s.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "asc" }));
  };
  const colSx = (c: InfCol<T>) => (c.width ? { width: c.width, flexShrink: 0 } : { flex: c.flex ?? 1, minWidth: c.minWidth ?? 0 });
  const justify = (a?: string) => (a === "right" ? "flex-end" : a === "center" ? "center" : "flex-start");

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1.2, borderBottom: "2px solid", borderColor: "primary.main", bgcolor: "#FAFAFA", position: "sticky", top: 0, zIndex: 2 }}>
        {numbered && <Box sx={{ ...NUM_COL_SX, fontWeight: 700, fontSize: "0.78rem", color: "text.secondary" }}>#</Box>}
        {columns.map((c) => (
          <Box key={c.key} onClick={() => onSort(c)}
            sx={{ ...colSx(c), display: "flex", alignItems: "center", gap: 0.3, justifyContent: justify(c.align), cursor: c.sortValue ? "pointer" : "default", userSelect: "none", fontWeight: 700, fontSize: "0.78rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.02em" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.header}</span>
            {sort?.key === c.key && (sort.dir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
          </Box>
        ))}
      </Box>
      <InfiniteList
        items={items}
        resetKey={`${resetKey}|${sort?.key ?? ""}|${sort?.dir ?? ""}`}
        keyOf={(it, i) => keyOf(it.row, i)}
        render={({ row, n, gapBefore }, i) => (
          <>
          {gapBefore && GROUP_GAP}
          <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, borderTop: i ? "1px solid rgba(0,0,0,.06)" : "none", "&:hover": { bgcolor: "rgba(0,0,0,.02)" } }}>
            {numbered && <Box sx={{ ...NUM_COL_SX, fontSize: "0.8rem", color: "text.secondary" }}>{n}</Box>}
            {columns.map((c) => (
              <Box key={c.key} sx={{ ...colSx(c), display: "flex", justifyContent: justify(c.align), minWidth: 0, fontSize: "0.875rem" }}>
                <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: c.align ?? "left" }}>{c.render(row)}</Box>
              </Box>
            ))}
          </Box>
          </>
        )}
      />
    </Box>
  );
}

const NUM_COL_SX = { width: 44, flexShrink: 0, textAlign: "right", pr: 1.5 } as const;
const periodOf = (u: { period: string }) => u.period;
const numPrefix = (n: number) => <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, mr: 0.75 }}>{n}.</Box>;

// Centra verticalmente il contenuto di OGNI cella (anche quelle con renderCell),
// mantenendo l'allineamento orizzontale di colonna (right/left/center).
const GRID_SX = {
  border: "none",
  "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
  "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
  "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
  "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
} as const;

const CURRENT_YEAR = new Date().getFullYear();
const PERIODS: string[] = [];
for (let y = 2024; y <= CURRENT_YEAR + 2; y++) {
  for (let m = 1; m <= 12; m++) PERIODS.push(`${String(m).padStart(2, "0")}/${y}`);
}

// Ordine richiesto ovunque: Proprietario → Cavallo → Pony → (Extra/Altro).
const PARTNER_TYPE_KEYS = ["form.partnerType.owner", "form.partnerType.horse", "form.partnerType.pony"];
const TYPE_ORDER = ["owner", "horse", "pony", "other"];
const typeRank = (key?: string) => {
  const suffix = (key ?? "").split(".").pop() ?? "";
  const i = TYPE_ORDER.indexOf(suffix);
  return i === -1 ? 99 : i;
};
const SERVICE_COLORS = ["#1565C0", "#6A1B9A", "#2E7D32", "#E65100", "#00838F", "#C62828", "#5D4037"];

interface PartnerTypeStat {
  partnerType: string;
  courseCount: number; courseAmount: number;
  extraCount: number; extraAmount: number;
  payedCount: number; payedAmount: number;
  dueTotal: number;
}
interface StatDetailRow {
  partnerType: string; kind: "course" | "extra"; serviceType: string; description: string; count: number; due: number;
}
interface UnpaidReceiptRow { personId: string; period: string; receiptsNumber: string; due: number; collected: number }
interface ServiceTypeStat { serviceType: string; count: number; amount: number }
interface ServiceDetailRow { serviceType: string; description: string; count: number; amount: number }
interface PerPartnerStat {
  personId: string; partnerType: string; mainCourseName: string;
  courseCount: number; courseAmount: number; extraCount: number; extraAmount: number;
  paidCount: number; paidAmount: number; balance: number;
}
interface PeriodStatistics {
  period: string;
  byPartnerType: PartnerTypeStat[];
  totals: { courseCount: number; courseAmount: number; extraCount: number; extraAmount: number; payedCount: number; payedAmount: number; dueTotal: number };
  unpaid: { totalDue: number; collected: number; balance: number; unsettledSheets: number; totalSheets: number };
  unpaidReceipts: UnpaidReceiptRow[];
  details: StatDetailRow[];
  byServiceType: ServiceTypeStat[];
  serviceDetails: ServiceDetailRow[];
  perPartner: PerPartnerStat[];
}

type TabKey = "soci" | "servizi" | "insoluti" | "sommario";

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december"
];

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color ?? "text.primary"}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export function StatisticsPage() {
  const { t } = useTranslation();
  const { call, openPdf, printPdf } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [period, setPeriod] = useState(currentPeriod());
  const [partnerType, setPartnerType] = useState("");
  const [tab, setTab] = useState<TabKey>("soci");
  const [stats, setStats] = useState<PeriodStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<PartnerReduced[]>([]);
  // Filtri specifici Insoluti
  const [insolutiActive, setInsolutiActive] = useState<"all" | "true" | "false">("all");
  // Ambito insoluti: "period" = solo periodo; "year" = periodo + mesi precedenti dello stesso anno;
  // "previous" = periodo + tutti i precedenti. Sempre FINO al periodo selezionato. Default: "period".
  const [scope, setScope] = useState<"period" | "year" | "previous">("period");
  // Paginazione liste lunghe su mobile (solo display).

  useEffect(() => {
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all", { silent: true })
      .then((r) => setPartners(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setPartners([]));
  }, [call]);

  const partnerById = useMemo(() => {
    const m = new Map<string, PartnerReduced>();
    partners.forEach((p) => p._id && m.set(String(p._id), p));
    return m;
  }, [partners]);

  // Etichette dinamiche dell'ambito insoluti, basate sul periodo selezionato (es. 06/2026):
  // "Solo 06/2026" · "Gen–Giu 2026" (gennaio→mese selezionato) · "Fino a 06/2026".
  const selMonth = Number(period.split("/")[0]) || 1;
  const selYear = period.split("/")[1] ?? "";
  const shortMon = (m: number) => t(`form.month.${MONTH_KEYS[Math.min(Math.max(m, 1), 12) - 1]}`).slice(0, 3);
  const scopeYearLabel = selMonth <= 1 ? `${shortMon(1)} ${selYear}` : `${shortMon(1)}–${shortMon(selMonth)} ${selYear}`;
  const scopeLabel = (s: "period" | "year" | "previous") =>
    s === "period" ? t("stats.scopeOnly", { period }) : s === "year" ? scopeYearLabel : t("stats.scopeUntil", { period });

  const fetchStats = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ period });
    if (partnerType) qs.set("partnerType", partnerType);
    qs.set("scope", scope);
    call<PeriodStatistics>(`/api/v1/payments/credits/statistics/full?${qs}`)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [call, period, partnerType, scope]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const pdfPath = () => {
    const qs = new URLSearchParams({ period, tab });
    if (partnerType) qs.set("partnerType", partnerType);
    if (tab === "insoluti") {
      qs.set("scope", scope);
      if (insolutiActive !== "all") qs.set("active", insolutiActive);
    }
    return `/api/v1/payments/credits/statistics/full/pdf?${qs}`;
  };
  const exportPdf = () => openPdf(pdfPath());
  const printList = () => printPdf(pdfPath());

  const ptName = (k: string) => formLabel(k);
  // Nei servizi il tipo "altro" va mostrato come "Extra".
  const svcName = (k: string) => (k === "form.service.other" ? t("stats.extra") : formLabel(k));

  // Ordinamento coerente Proprietario → Cavallo → Pony → (Extra) ovunque.
  const byPartnerTypeOrdered = useMemo(
    () => [...(stats?.byPartnerType ?? [])].sort((a, b) => typeRank(a.partnerType) - typeRank(b.partnerType)),
    [stats]
  );
  const byServiceTypeOrdered = useMemo(
    () => [...(stats?.byServiceType ?? [])].sort((a, b) => typeRank(a.serviceType) - typeRank(b.serviceType)),
    [stats]
  );

  const detailRows = useMemo(
    () => [...(stats?.details ?? [])]
      .sort((a, b) => typeRank(a.partnerType) - typeRank(b.partnerType) || (a.kind === b.kind ? b.due - a.due : a.kind === "course" ? -1 : 1))
      .map((d, i) => ({ id: i, ...d })),
    [stats]
  );

  // Insoluti: TUTTI i periodi, filtrati per tipo socio (via anagrafica), raggruppati per periodo.
  const periodRank = (p: string) => { const [mm, yyyy] = p.split("/"); return (Number(yyyy) || 0) * 100 + (Number(mm) || 0); };
  const unpaidFiltered = useMemo(() => {
    const list = (stats?.unpaidReceipts ?? []).filter((u) => {
      const p = partnerById.get(u.personId);
      if (partnerType && p?.partnerType !== partnerType) return false;
      if (insolutiActive === "true" && p?.active === false) return false;
      if (insolutiActive === "false" && p?.active !== false) return false;
      return true;
    });
    const totalDue = list.reduce((s, u) => s + u.due + u.collected, 0); // due = scoperto; credito = due+collected
    const collected = list.reduce((s, u) => s + u.collected, 0);
    // Raggruppa per periodo
    const groups = new Map<string, typeof list>();
    list.forEach((u) => { const a = groups.get(u.period) ?? []; a.push(u); groups.set(u.period, a); });
    const byPeriod = [...groups.entries()]
      .sort((a, b) => periodRank(a[0]) - periodRank(b[0]))
      .map(([period, rows]) => ({
        period,
        rows: rows
          .map((u, i) => ({ id: `${period}-${i}`, ...u }))
          .sort((a, b) => (partnerById.get(a.personId)?.fullName ?? "").localeCompare(partnerById.get(b.personId)?.fullName ?? "", "it")),
        due: rows.reduce((s, u) => s + u.due, 0)
      }));
    return { byPeriod, totalDue, collected, balance: list.reduce((s, u) => s + u.due, 0), count: list.length };
  }, [stats, partnerType, partnerById, insolutiActive]);

  // Lista piatta degli insoluti (tutti i periodi), ordinata per periodo desc poi nome.
  const unpaidFlat = useMemo(() => {
    const flat = unpaidFiltered.byPeriod.flatMap((g) => g.rows);
    return [...flat].sort((a, b) => {
      const d = periodRank(b.period) - periodRank(a.period);
      return d !== 0 ? d : (partnerById.get(a.personId)?.fullName ?? "").localeCompare(partnerById.get(b.personId)?.fullName ?? "", "it");
    });
  }, [unpaidFiltered, partnerById]);

  const hasData = stats && (stats.byPartnerType.length > 0 || stats.unpaid.totalSheets > 0);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <PageHeader
        title={t("stats.title")}
        subtitle={t("stats.subtitle")}
        onRefresh={fetchStats}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 130 }} data-tour="st-period">
              <InputLabel>{t("common.period")}</InputLabel>
              <Select label={t("common.period")} value={period} onChange={(e) => setPeriod(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <Button data-tour="st-actions" variant="outlined" size="small" startIcon={<PrintIcon />} onClick={printList} disabled={!hasData}>
              {t("common.print")}
            </Button>
            <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={exportPdf} disabled={!hasData}>
              {t("common.exportPdf")}
            </Button>
          </Stack>
        }
      />

      {/* Filtro tipo socio + tab — FISSI */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, flexShrink: 0 }}>
        <ToggleButtonGroup
          data-tour="st-types"
          exclusive size="small" color="primary"
          value={partnerType}
          onChange={(_, v) => v !== null && setPartnerType(v)}
          sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="">{t("stats.all")}</ToggleButton>
          {PARTNER_TYPE_KEYS.map((k) => (
            <ToggleButton key={k} value={k}>{ptName(k)}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, flexShrink: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}>
          <Tab value="soci" label={t("stats.tabSoci")} data-tour="st-tab-soci" />
          <Tab value="servizi" label={t("stats.tabServizi")} data-tour="st-tab-servizi" />
          <Tab value="insoluti" label={t("stats.tabInsoluti")} data-tour="st-tab-insoluti" />
          <Tab value="sommario" label={t("stats.tabSommario")} data-tour="st-tab-sommario" />
        </Tabs>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, pb: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {loading && <Box sx={{ mb: 2 }}><LinearProgress /></Box>}
        {!loading && !hasData && <EmptyState title={t("stats.noData")} />}

        {!loading && hasData && stats && (
          <>
            {/* RIEPILOGHI — FISSI */}
            <Box sx={{ flexShrink: 0 }} data-tour="st-kpi">
              {(tab === "soci" || tab === "sommario") && (
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" } }}>
                  <StatCard label={t("stats.courses")} value={eur(stats.totals.courseAmount)} sub={`${stats.totals.courseCount} ${t("stats.units")}`} color="#E65100" />
                  <StatCard label={t("stats.extra")} value={eur(stats.totals.extraAmount)} sub={`${stats.totals.extraCount} ${t("stats.units")}`} color="#6A1B9A" />
                  <StatCard label={t("stats.total")} value={eur(stats.totals.courseAmount + stats.totals.extraAmount)} sub={`${stats.totals.courseCount + stats.totals.extraCount} ${t("stats.units")}`} color="#1565C0" />
                  <StatCard label={tab === "sommario" ? t("stats.paid") : t("stats.collected")} value={eur(stats.totals.payedAmount)} sub={`${stats.totals.payedCount} ${t("stats.units")}`} color="#2E7D32" />
                  <StatCard label={t("stats.balance")} value={eur(stats.totals.courseAmount + stats.totals.extraAmount - stats.totals.payedAmount)} sub={t("stats.due")} color="#C62828" />
                </Box>
              )}
              {tab === "servizi" && (
                <Grid container spacing={2}>
                  {byServiceTypeOrdered.map((st) => (
                    <Grid item xs={12} sm={6} md={3} key={st.serviceType}>
                      <StatCard label={svcName(st.serviceType)} value={eur(st.amount)} sub={`${st.count} ${t("stats.units")}`} color={SERVICE_COLORS[typeRank(st.serviceType) % SERVICE_COLORS.length]} />
                    </Grid>
                  ))}
                </Grid>
              )}
              {tab === "insoluti" && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>{t("receipts.statusPartner")}</Typography>
                      <ToggleButtonGroup exclusive size="small" color="primary" value={insolutiActive} onChange={(_, v) => v !== null && setInsolutiActive(v)}>
                        <ToggleButton value="all">{t("common.all")}</ToggleButton>
                        <ToggleButton value="true">{t("receipts.activePartners")}</ToggleButton>
                        <ToggleButton value="false">{t("receipts.inactivePartners")}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>{t("stats.scope")}</Typography>
                      <ToggleButtonGroup exclusive size="small" color="primary" value={scope} onChange={(_, v) => v !== null && setScope(v)}>
                        <ToggleButton value="period">{scopeLabel("period")}</ToggleButton>
                        <ToggleButton value="year">{scopeLabel("year")}</ToggleButton>
                        <ToggleButton value="previous">{scopeLabel("previous")}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}><StatCard label={t("stats.totalDue")} value={eur(unpaidFiltered.totalDue)} color="#E65100" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard label={t("stats.collected")} value={eur(unpaidFiltered.collected)} color="#2E7D32" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard label={t("stats.balance")} value={eur(unpaidFiltered.balance)} color="#C62828" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><StatCard label={t("stats.unsettledSheets")} value={`${unpaidFiltered.count}`} color="#6A1B9A" /></Grid>
                  </Grid>
                </>
              )}
            </Box>

            {/* DETTAGLI — SCORRIBILI */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", mt: 2, pr: 0.5 }} data-tour="st-content">
              {tab === "soci" && (
                <>
                  <Card sx={{ mb: 2 }}>
                    <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
                      <Typography variant="subtitle1" fontWeight={700}>{t("stats.summaryByType")}</Typography>
                    </Box>
                    {isMobile ? (
                      <Box>
                        {byPartnerTypeOrdered.map((p, idx) => (
                          <TypeSummaryMobileRow key={p.partnerType} label={ptName(p.partnerType)}
                            courseCount={p.courseCount} courseAmount={p.courseAmount}
                            extraCount={p.extraCount} extraAmount={p.extraAmount}
                            payedCount={p.payedCount} payedAmount={p.payedAmount}
                            t={t} divider={idx > 0} />
                        ))}
                        <TypeSummaryMobileRow label={t("stats.total")} total
                          courseCount={stats.totals.courseCount} courseAmount={stats.totals.courseAmount}
                          extraCount={stats.totals.extraCount} extraAmount={stats.totals.extraAmount}
                          payedCount={stats.totals.payedCount} payedAmount={stats.totals.payedAmount}
                          t={t} divider />
                      </Box>
                    ) : (
                    <DataGrid
                      autoHeight disableRowSelectionOnClick hideFooter sx={{ ...GRID_SX, "& .stats-total-row": { fontWeight: 700, bgcolor: "#F0F4F0" } }}
                      getRowId={(r) => r.partnerType}
                      getRowClassName={(p) => p.row.partnerType === "__total" ? "stats-total-row" : ""}
                      rows={[
                        ...byPartnerTypeOrdered.map((p) => ({ ...p, total: p.courseAmount + p.extraAmount })),
                        {
                          partnerType: "__total",
                          courseCount: stats.totals.courseCount, courseAmount: stats.totals.courseAmount,
                          extraCount: stats.totals.extraCount, extraAmount: stats.totals.extraAmount,
                          payedCount: stats.totals.payedCount, payedAmount: stats.totals.payedAmount,
                          dueTotal: stats.totals.dueTotal, total: stats.totals.courseAmount + stats.totals.extraAmount
                        }
                      ]}
                      columns={[
                        { field: "partnerType", headerName: t("stats.partnerType"), flex: 1, minWidth: 140, renderCell: (p: GridRenderCellParams<PartnerTypeStat>) => p.row.partnerType === "__total" ? <Typography variant="body2" fontWeight={700}>{t("stats.total")}</Typography> : <Typography variant="body2" fontWeight={600}>{ptName(p.row.partnerType)}</Typography> },
                        { field: "courseCount", headerName: t("stats.nCol"), width: 55, align: "right", headerAlign: "right" },
                        { field: "courseAmount", headerName: t("stats.coursesCol"), width: 110, align: "right", headerAlign: "right", renderCell: (p: GridRenderCellParams<PartnerTypeStat>) => eur(p.row.courseAmount) },
                        { field: "extraCount", headerName: t("stats.nCol"), width: 55, align: "right", headerAlign: "right" },
                        { field: "extraAmount", headerName: t("stats.extraCol"), width: 110, align: "right", headerAlign: "right", renderCell: (p: GridRenderCellParams<PartnerTypeStat>) => eur(p.row.extraAmount) },
                        { field: "total", headerName: t("stats.total"), width: 120, align: "right", headerAlign: "right", renderCell: (p: GridRenderCellParams<PartnerTypeStat & { total: number }>) => <Typography variant="body2" fontWeight={700}>{eur(p.row.total)}</Typography> },
                        { field: "payedCount", headerName: t("stats.nCol"), width: 55, align: "right", headerAlign: "right" },
                        { field: "payedAmount", headerName: t("stats.paidCol"), width: 120, align: "right", headerAlign: "right", renderCell: (p: GridRenderCellParams<PartnerTypeStat>) => <Typography variant="body2" color="success.dark" fontWeight={600}>{eur(p.row.payedAmount)}</Typography> }
                      ]}
                    />
                    )}
                  </Card>
                  {isMobile
                    ? <DetailListMobile rows={detailRows} t={t} ptName={ptName} />
                    : <DetailGrid rows={detailRows} t={t} ptName={ptName} />}
                </>
              )}

              {tab === "servizi" && (
                <Card>
                  <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
                    <Typography variant="subtitle1" fontWeight={700}>{t("stats.detailTitle")}</Typography>
                  </Box>
                  {isMobile ? (
                    <Box>
                      <InfiniteList
                        items={[...stats.serviceDetails].sort((a, b) => typeRank(a.serviceType) - typeRank(b.serviceType))}
                        resetKey={`svc|${period}|${partnerType}`}
                        keyOf={(_d, i) => i}
                        render={(d, idx) => (
                          <Box sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none", display: "flex", justifyContent: "space-between", gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>{numPrefix(idx + 1)}{d.description}</Typography>
                              <Typography variant="caption" color="text.secondary">{svcName(d.serviceType)} · {d.count} {t("stats.units")}</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>{eur(d.amount)}</Typography>
                          </Box>
                        )}
                      />
                    </Box>
                  ) : (
                  <SortableInfiniteTable
                    numbered
                    rows={[...stats.serviceDetails].sort((a, b) => typeRank(a.serviceType) - typeRank(b.serviceType))}
                    resetKey={`svc|${period}|${partnerType}`}
                    keyOf={(_d, i) => i}
                    columns={[
                      { key: "serviceType", header: t("stats.serviceType"), width: 150, render: (d) => svcName(d.serviceType), sortValue: (d) => svcName(d.serviceType) },
                      { key: "description", header: t("common.description"), flex: 1, minWidth: 220, render: (d) => d.description, sortValue: (d) => d.description },
                      { key: "count", header: t("stats.units"), width: 90, align: "right", render: (d) => d.count, sortValue: (d) => d.count },
                      { key: "amount", header: t("stats.amount"), width: 130, align: "right", render: (d) => <Typography variant="body2" fontWeight={600}>{eur(d.amount)}</Typography>, sortValue: (d) => d.amount }
                    ]}
                  />
                  )}
                </Card>
              )}

              {tab === "insoluti" && (
                <Card>
                  <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
                    <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>
                      {t("stats.unsettledList")} — {scopeLabel(scope)}
                    </Typography>
                  </Box>
                  {isMobile ? (
                    <Box>
                      <InfiniteList
                        items={numberRows(unpaidFlat, periodOf)}
                        resetKey={`ins|${period}|${partnerType}|${scope}|${insolutiActive}`}
                        keyOf={({ row: u }) => u.id}
                        render={({ row: u, n, gapBefore }, idx) => (
                          <>
                          {gapBefore && GROUP_GAP}
                          <Box sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none", display: "flex", justifyContent: "space-between", gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>{numPrefix(n)}{partnerById.get(u.personId)?.fullName ?? u.personId}</Typography>
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 0.25, flexWrap: "wrap", alignItems: "center" }}>
                                <Chip size="small" variant="outlined" label={ptName(partnerById.get(u.personId)?.partnerType ?? "")} sx={{ fontSize: "0.68rem" }} />
                                <Chip size="small" variant="outlined" label={u.period} sx={{ fontSize: "0.68rem" }} />
                              </Stack>
                              <Typography variant="caption" color="text.secondary">{t("stats.sheet")} {u.receiptsNumber}</Typography>
                            </Box>
                            <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} color="warning.main">{eur(u.due)}</Typography>
                              <Typography variant="caption" color="text.secondary">{t("stats.collected")}: {eur(u.collected)}</Typography>
                            </Box>
                          </Box>
                          </>
                        )}
                      />
                    </Box>
                  ) : (
                    <SortableInfiniteTable
                      numbered
                      groupOf={periodOf}
                      rows={unpaidFlat}
                      resetKey={`ins|${period}|${partnerType}|${scope}|${insolutiActive}`}
                      keyOf={(u) => u.id}
                      columns={[
                        { key: "period", header: t("common.period"), width: 90, render: (u) => u.period },
                        { key: "partnerType", header: t("stats.partnerType"), width: 120, render: (u) => ptName(partnerById.get(u.personId)?.partnerType ?? ""), sortValue: (u) => ptName(partnerById.get(u.personId)?.partnerType ?? "") },
                        { key: "socio", header: t("stats.socio"), flex: 1, minWidth: 170, render: (u) => <Typography variant="body2" fontWeight={600}>{partnerById.get(u.personId)?.fullName ?? u.personId}</Typography>, sortValue: (u) => partnerById.get(u.personId)?.fullName ?? u.personId },
                        { key: "contact", header: t("stats.contact"), width: 150, render: (u) => partnerById.get(u.personId)?.contact ?? "", sortValue: (u) => partnerById.get(u.personId)?.contact ?? "" },
                        { key: "receiptsNumber", header: t("stats.sheet"), width: 120, render: (u) => u.receiptsNumber, sortValue: (u) => u.receiptsNumber },
                        { key: "due", header: t("stats.due"), width: 120, align: "right", render: (u) => <Typography variant="body2" fontWeight={600} color="warning.main">{eur(u.due)}</Typography>, sortValue: (u) => u.due },
                        { key: "collected", header: t("stats.collected"), width: 110, align: "right", render: (u) => eur(u.collected), sortValue: (u) => u.collected }
                      ]}
                    />
                  )}
                </Card>
              )}

              {tab === "sommario" && (
                <Card>
                  <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
                    <Typography variant="subtitle1" fontWeight={700}>{t("stats.perPartnerTitle")}</Typography>
                  </Box>
                  {isMobile ? (
                    <Box>
                      <InfiniteList
                        items={[...stats.perPartner].sort((a, b) => (partnerById.get(a.personId)?.fullName ?? "").localeCompare(partnerById.get(b.personId)?.fullName ?? "", "it"))}
                        resetKey={`som|${period}|${partnerType}`}
                        keyOf={(p) => p.personId}
                        render={(p, idx) => (
                          <Box sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700}>{numPrefix(idx + 1)}{partnerById.get(p.personId)?.fullName ?? p.personId}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {ptName(p.partnerType)}{partnerById.get(p.personId)?.contact ? ` · ${partnerById.get(p.personId)?.contact}` : ""}
                                </Typography>
                                {p.mainCourseName && <Typography variant="body2" sx={{ mt: 0.5 }}>{p.mainCourseName}</Typography>}
                              </Box>
                              <Typography variant="body2" fontWeight={700} color={p.balance > 0 ? "warning.main" : "text.primary"} sx={{ whiteSpace: "nowrap" }}>{eur(p.balance)}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                              <Chip size="small" variant="outlined" label={`${t("stats.courses")}: ${eur(p.courseAmount)}`} sx={{ fontSize: "0.7rem" }} />
                              <Chip size="small" variant="outlined" label={`${t("stats.extra")}: ${eur(p.extraAmount)} (${p.extraCount})`} sx={{ fontSize: "0.7rem" }} />
                              <Chip size="small" variant="outlined" color="success" label={`${t("stats.paid")}: ${eur(p.paidAmount)}`} sx={{ fontSize: "0.7rem" }} />
                            </Stack>
                          </Box>
                        )}
                      />
                    </Box>
                  ) : (
                  <SortableInfiniteTable
                    numbered
                    rows={[...stats.perPartner].sort((a, b) => (partnerById.get(a.personId)?.fullName ?? "").localeCompare(partnerById.get(b.personId)?.fullName ?? "", "it"))}
                    resetKey={`som|${period}|${partnerType}`}
                    keyOf={(p) => p.personId}
                    columns={[
                      { key: "partnerType", header: t("stats.partnerType"), width: 130, render: (p) => ptName(p.partnerType), sortValue: (p) => ptName(p.partnerType) },
                      { key: "socio", header: t("stats.socio"), flex: 1, minWidth: 170, render: (p) => <Typography variant="body2" fontWeight={600}>{partnerById.get(p.personId)?.fullName ?? p.personId}</Typography>, sortValue: (p) => partnerById.get(p.personId)?.fullName ?? p.personId },
                      { key: "contact", header: t("stats.contact"), width: 150, render: (p) => partnerById.get(p.personId)?.contact ?? "", sortValue: (p) => partnerById.get(p.personId)?.contact ?? "" },
                      { key: "mainCourseName", header: t("stats.courseName"), flex: 1, minWidth: 170, render: (p) => p.mainCourseName, sortValue: (p) => p.mainCourseName ?? "" },
                      { key: "courseAmount", header: t("stats.courses"), width: 100, align: "right", render: (p) => eur(p.courseAmount), sortValue: (p) => p.courseAmount },
                      { key: "extraCount", header: "N.Ex", width: 60, align: "right", render: (p) => p.extraCount, sortValue: (p) => p.extraCount },
                      { key: "extraAmount", header: t("stats.extra"), width: 100, align: "right", render: (p) => eur(p.extraAmount), sortValue: (p) => p.extraAmount },
                      { key: "paidAmount", header: t("stats.paid"), width: 100, align: "right", render: (p) => <Typography variant="body2" color="success.dark">{eur(p.paidAmount)}</Typography>, sortValue: (p) => p.paidAmount },
                      { key: "balance", header: t("stats.balance"), width: 110, align: "right", render: (p) => <Typography variant="body2" fontWeight={700} color={p.balance > 0 ? "warning.main" : "text.primary"}>{eur(p.balance)}</Typography>, sortValue: (p) => p.balance }
                    ]}
                  />
                  )}
                </Card>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// Riga riepilogo per tipo socio — variante mobile (impilata).
function TypeSummaryMobileRow({ label, total, courseCount, courseAmount, extraCount, extraAmount, payedCount, payedAmount, t, divider }: {
  label: string; total?: boolean;
  courseCount: number; courseAmount: number; extraCount: number; extraAmount: number;
  payedCount: number; payedAmount: number; t: (k: string) => string; divider: boolean;
}) {
  const grand = courseAmount + extraAmount;
  return (
    <Box sx={{ p: 2, borderTop: divider ? "1px solid rgba(0,0,0,.06)" : "none", bgcolor: total ? "#F0F4F0" : "transparent" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
        <Typography variant="body2" fontWeight={700}>{label}</Typography>
        <Typography variant="body2" fontWeight={700}>{eur(grand)}</Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary">{t("stats.courses")}: {eur(courseAmount)} ({courseCount})</Typography>
        <Typography variant="caption" color="text.secondary">·</Typography>
        <Typography variant="caption" color="text.secondary">{t("stats.extra")}: {eur(extraAmount)} ({extraCount})</Typography>
        <Typography variant="caption" color="success.dark" fontWeight={600}>· {t("stats.collected")}: {eur(payedAmount)} ({payedCount})</Typography>
      </Stack>
    </Box>
  );
}

// Dettaglio competenze (corsi/extra) — variante mobile (impilata).
function DetailListMobile({ rows, t, ptName }: {
  rows: (StatDetailRow & { id: number })[];
  t: (k: string) => string;
  ptName: (k: string) => string;
}) {
  return (
    <Card>
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
        <Typography variant="subtitle1" fontWeight={700}>{t("stats.detailTitle")}</Typography>
      </Box>
      <InfiniteList
        items={rows}
        resetKey={rows.length}
        keyOf={(d) => d.id}
        render={(d, idx) => (
          <Box sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>{numPrefix(idx + 1)}{d.description}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>{eur(d.due)}</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              <Chip size="small" variant="outlined" label={ptName(d.partnerType)} sx={{ fontSize: "0.68rem" }} />
              <Chip size="small" variant="outlined" label={d.kind === "extra" ? t("stats.extra") : t("stats.courses")} sx={{ fontSize: "0.68rem" }} />
              <Typography variant="caption" color="text.secondary">×{d.count}</Typography>
            </Stack>
          </Box>
        )}
      />
    </Card>
  );
}

function DetailGrid({ rows, t, ptName }: {
  rows: (StatDetailRow & { id: number })[];
  t: (k: string) => string;
  ptName: (k: string) => string;
}) {
  return (
    <Card>
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
        <Typography variant="subtitle1" fontWeight={700}>{t("stats.detailTitle")}</Typography>
      </Box>
      <SortableInfiniteTable
        numbered
        rows={rows}
        resetKey={rows.length}
        keyOf={(d) => d.id}
        columns={[
          { key: "partnerType", header: t("stats.partnerType"), width: 150, render: (d) => ptName(d.partnerType), sortValue: (d) => ptName(d.partnerType) },
          { key: "kind", header: t("stats.courseExtra"), width: 110, render: (d) => d.kind === "extra" ? t("stats.extra") : t("stats.courses"), sortValue: (d) => d.kind },
          { key: "description", header: t("common.description"), flex: 1, minWidth: 220, render: (d) => d.description, sortValue: (d) => d.description },
          { key: "count", header: t("stats.quantity"), width: 100, align: "right", render: (d) => d.count, sortValue: (d) => d.count },
          { key: "due", header: t("stats.due"), width: 130, align: "right", render: (d) => <Typography variant="body2" fontWeight={600}>{eur(d.due)}</Typography>, sortValue: (d) => d.due }
        ]}
      />
    </Card>
  );
}
